import React, { useState, useEffect, Fragment, useContext } from "react";
import { SortableContainer, SortableElement } from "react-sortable-hoc";
import { EditModeContext } from "../../context/EditModeContext";
import { SubdestajosContext } from "../../context/SubdestajosContext";
import { ProyectosContext } from "../../context/ProyectosContext";
import { ModalContext } from "../../context/ModalContext";
import SubdestajoTabla from "./SubdestajoTabla";
import SubdestajoForm from "./SubdestajoForm";
import Rubro from "../common/Rubro";
import TextoExpandible from "../common/TextoExpandible";
import { getValue } from "../../utils";
import { AppConfigContext } from "../../context/AppConfigContext";
import { DestajosContext } from "../../context/DestajosContext";
import { AvanceModeContext } from "../../context/AvanceModeContext";
import AvancePickerMobile from "../cronograma/AvancePickerMobile";
import BitacoraConcepto from "../conceptos/BitacoraConceptos";
import OffCanvas from "../global/OffCanvas";
import DestajoHistorial from "./DestajoHistorial";
import { renderNombreDestajo } from "../../utils/destajos";
import { compararPorOrden } from "../../utils/orden";

const SubdestajosContainer = SortableContainer((props) =>
  props.renderSubdestajos()
);

const SubDestajoFila = SortableElement((props) => (
  <SubdestajoForm
    idProyecto={props.idProyecto}
    subdestajo={props.subdestajo}
    idDestajo={props.idDestajo}
    key={"subdestajo-" + props.subdestajo.idSubDestajo}
    setPropiedadSubDestajo={props.setPropiedadSubDestajo}
    deleteSubDestajo={props.deleteSubDestajo}
    confirm={props.confirm}
  />
));

const DestajoTabla = ({ destajo, getProyecto, forceOpen, busqueda }) => {
  const [subdestajosDestajo, setSubdestajosDestajo] = useState(null);
  const [showSubDestajos, setShowSubDestajos] = useState(false);
  const [showHistorial, setShowHistorial] = useState(false);

  const {
    subdestajos,
    postSubDestajos,
    deleteSubDestajo,
    reordenarSubdestajos,
    postAvanceSubDestajo,
    createNuevoSubDestajo,
    setPropiedadSubDestajo,
  } = useContext(SubdestajosContext);

  const concepto =
    Array.isArray(destajo.conceptos) && destajo.conceptos.length > 0
      ? destajo.conceptos[0]
      : null;

  const conceptoCantidad = concepto ? Number(concepto.cantidad) : NaN;

  // Accumulated quantity of the concepto's unit (progreso is a 0-1 fraction).
  const acumulado =
    Math.round((parseFloat(destajo.progreso) || 0) * conceptoCantidad * 100) /
    100;

  const appconfig = useContext(AppConfigContext);

  const { postAvanceDestajo } = useContext(DestajosContext);

  const { editMode, editModeOn, viewLevel } = useContext(EditModeContext);
  const { proyecto } = useContext(ProyectosContext);
  const { confirm, modalComponent } = useContext(ModalContext);
  const { mode, inputType } = useContext(AvanceModeContext);
  const { idProyecto, permiso, idPermiso } = proyecto;

  // Destajos only take avance when the project tracks progress at the destajo level.
  const showAvancePicker = (proyecto.modo_avance || "destajo") === "destajo";

  // The global toggle picks the mode; amounts only apply to destajos that have a
  // concepto with a positive cantidad to measure against (others stay %).
  const unitMode = mode === "amount" && !!concepto && conceptoCantidad > 0;

  console.log("DestajoTabla permiso:", permiso, "idPermiso:", idPermiso);

  function canEditProgress() {
    return (
      ["admin", "supervisor", "contratista"].includes(permiso) ||
      [3, 6].includes(parseInt(idPermiso))
    );
  }

  function handleComments() {
    if (!concepto) return;
    modalComponent(
      "Comentarios",
      <BitacoraConcepto
        idConcepto={concepto.idConcepto}
        onChange={getProyecto}
      />
    );
  }

  function postAvance(cont, isManual) {
    postAvanceDestajo(
      destajo.idDestajo,
      cont,
      idProyecto,
      getProyecto,
      isManual,
      unitMode ? concepto.idConcepto : undefined,
      unitMode
    );
  }

  useEffect(() => {
    if (Array.isArray(subdestajos)) {
      handleSubdestajos();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [subdestajos]);

  // Opened/closed by the search bar (undefined when there is no search).
  useEffect(() => {
    setShowSubDestajos(Boolean(forceOpen));
  }, [forceOpen]);

  function handleSubdestajos() {
    let current = subdestajos
      .filter((subdestajo) => subdestajo.idDestajo === destajo.idDestajo)
      .sort((a, b) => compararPorOrden(a, b, "idSubDestajo"));
    setSubdestajosDestajo(current);
  }

  function toggleSubdestajos() {
    setShowSubDestajos(!showSubDestajos);
    if (!showSubDestajos) viewLevel("subdestajos");
    else viewLevel("destajos");
  }

  function handleCreate() {
    createNuevoSubDestajo(destajo.idDestajo, subdestajosDestajo.length);
  }

  function handlePostSubdestajos() {
    postSubDestajos(idProyecto, subdestajosDestajo, getProyecto);
  }

  function renderSubDestajos() {
    if (showSubDestajos) {
      return (
        <SubdestajosContainer
          renderSubdestajos={renderSubdestajos}
          showSubDestajos={showSubDestajos}
          toggleSubdestajos={toggleSubdestajos}
          onSortEnd={reordenarSubdestajos}
          pressDelay={100}
          useDragHandle
        />
      );
    }
  }

  function renderSubdestajos() {
    if (Array.isArray(subdestajosDestajo)) {
      let subdestajos = subdestajosDestajo.filter(
        (subdestajo) => String(subdestajo.idSubDestajo)[0] !== "d"
      );
      // Searching: only the matching subdestajos, unless the destajo itself matches.
      if (
        busqueda &&
        editMode !== "des-" + destajo.idDestajo &&
        !busqueda.destajosCoinciden.has(destajo.idDestajo)
      ) {
        subdestajos = subdestajos.filter(({ idSubDestajo }) =>
          busqueda.subdestajosVisibles.has(idSubDestajo)
        );
      }
      if (editMode === "des-" + destajo.idDestajo) {
        return subdestajos.map((subdestajo, index) => (
          <SubDestajoFila
            index={index}
            idProyecto={idProyecto}
            subdestajo={subdestajo}
            idDestajo={destajo.idDestajo}
            key={"subdestajo-" + subdestajo.idSubDestajo}
            setPropiedadSubDestajo={setPropiedadSubDestajo}
            deleteSubDestajo={deleteSubDestajo}
            confirm={confirm}
          />
        ));
      } else {
        return subdestajos.map((subdestajo) => (
          <SubdestajoTabla
            permiso={permiso}
            idProyecto={idProyecto}
            subdestajo={subdestajo}
            getProyecto={getProyecto}
            key={"subdestajo-" + subdestajo.idSubDestajo}
            postAvanceSubDestajo={postAvanceSubDestajo}
          />
        ));
      }
    }
    return [];
  }

  function renderButtons() {
    let components = [];
    if (showSubDestajos) {
      if (editMode === "des-" + destajo.idDestajo) {
        components = [
          <button
            className="btn btn-primary me-2"
            onClick={handlePostSubdestajos}
            key={"save-subdestajos-" + destajo.idDestajo + "-btn"}
          >
            Guardar
          </button>,
          <button
            key={"add-subdestajos-" + destajo.idDestajo + "-btn"}
            className="btn btn-outline-primary"
            onClick={handleCreate}
          >
            <i className="fa fa-plus"></i>
            Agregar Subdestajo
          </button>,
          <button
            className="btn btn-outline-danger ms-2"
            key={"cancelDestajo" + destajo.idDestajo}
            onClick={() =>
              confirm(
                "¿Está seguro que desea cancelar? No se guardarán los cambios hechos a todos los Subdestajos del Destajo " +
                  destajo.nombre +
                  ".",
                getProyecto
              )
            }
          >
            Cancelar
          </button>,
        ];
      } else if (!editMode && ["admin", "coordinador"].includes(permiso)) {
        components = [
          <button
            onClick={() => editModeOn(`des-${destajo.idDestajo}`)}
            className="btn btn-outline-primary"
            key={"edit-destajos-" + destajo.idDestajo + "-btn"}
          >
            <i className="fa fa-edit me-2"></i>
            Editar Subdestajos
          </button>,
        ];
      }
      return (
        <tr>
          <td />
          <td style={{ minWidth: 330 }} colSpan="7">
            <div className="ps-5">{components}</div>
          </td>
        </tr>
      );
    }
  }

  // Contracted quantity; in percentage mode, preceded by the advance in units.
  const renderCantidad = () => {
    if (!concepto || concepto.cantidad == null) return "-";
    const cantidad = `${concepto.cantidad} ${concepto.unidad || ""}`.trim();
    if (unitMode || !(conceptoCantidad > 0)) return cantidad;
    return (
      <>
        <span className="text-primary bold">{acumulado}</span> / {cantidad}
      </>
    );
  };

  const renderProgress = () => {
    if (getValue(appconfig, "progress") === "money") {
      return `${parseInt(destajo.ejecutado * 100)}%`;
    }
    if (canEditProgress() && showAvancePicker) {
      if (unitMode) {
        return (
          <AvancePickerMobile
            permitirExcedente
            progreso={acumulado}
            setCantidad={postAvance}
            intervalo={1}
            limite={conceptoCantidad}
            inputType={inputType}
            suffix={concepto.unidad || ""}
          />
        );
      }
      return (
        <AvancePickerMobile
          permitirExcedente
          progreso={parseInt(destajo.progreso * 100)}
          setCantidad={postAvance}
          intervalo={1}
          limite={100}
          inputType={inputType}
        />
      );
    }
    if (unitMode) {
      return `${acumulado} ${concepto.unidad || ""}`.trim();
    }
    return `${parseInt(destajo.progreso * 100)}%`;
  };

  return (
    <Fragment>
      <tr
        className={`border-top bg-primary-light ${
          parseFloat(destajo.progreso) > 1 ? "concepto-aditivo" : ""
        }`}
      >
        <td className="ponderacion">
          <span className="small fw-normal">
            {destajo.ponderacion !== null &&
            destajo.ponderacion !== undefined &&
            !isNaN(parseInt(destajo.ponderacion))
              ? `${parseInt(destajo.ponderacion)}%`
              : "-"}
          </span>
        </td>
        <td className="rubro">
          <div className="row mx-0">
            <div className="col-2 px-0">
              <button
                className="btn btn-sm btn-light border"
                onClick={toggleSubdestajos}
              >
                <i
                  className={`fas fa-chevron-${
                    showSubDestajos ? "up" : "down"
                  } text-secondary toggle`}
                />
              </button>
            </div>
            <div className="col-10 px-0">
              <Rubro
                completado={destajo.completado}
                nombre={
                  <TextoExpandible>{renderNombreDestajo(destajo)}</TextoExpandible>
                }
                className="small fw-normal"
              />
            </div>
          </div>
        </td>
        <td
          className="text-center small fw-normal"
          style={{
            width: unitMode ? 190 : 160,
            minWidth: unitMode ? 190 : 160,
          }}
        >
          {renderProgress()}
        </td>
        <td className="text-center small fw-normal text-nowrap">
          {renderCantidad()}
        </td>
        <td>{getValue(destajo, "fecha_inicio", "date")}</td>
        <td>{getValue(destajo, "fecha_fin", "date")}</td>
        <td className="text-center">
          {concepto && (
            <button
              onClick={handleComments}
              className={`btn btn-sm btn-outline-${
                concepto.comentarios ? "primary" : "secondary"
              } me-2`}
            >
              <i className="fa fa-comment" />
              {concepto.comentarios > 0 ? ` ${concepto.comentarios}` : ""}
            </button>
          )}
          <button
            className="btn btn-sm btn-outline-secondary"
            onClick={() => setShowHistorial(true)}
            title="Historial de avances"
          >
            <i className="fa fa-list" />
          </button>
          <OffCanvas
            showOffCanvas={showHistorial}
            setShowOffCanvas={setShowHistorial}
          >
            {showHistorial && (
              <DestajoHistorial
                idProyecto={idProyecto}
                idDestajo={destajo.idDestajo}
                concepto={concepto}
                progreso={destajo.progreso}
              />
            )}
          </OffCanvas>
        </td>
      </tr>
      {renderSubDestajos()}
      {renderButtons()}
    </Fragment>
  );
};

export default DestajoTabla;
